import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import {
  ArrowLeft,
  Bell,
  Bot,
  GitBranch,
  Keyboard,
  LayoutGrid,
  Link2,
  LogOut,
  type LucideIcon,
  Mic,
  Monitor,
  Search,
  Settings,
  Sparkles,
  SquareUserRound,
  Unplug,
} from 'lucide-react';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import {
  availableSections,
  type SettingsSection,
  type SettingsSectionInfo,
  searchSettings,
} from '@/lib/settings-page/sections';

export const SECTION_ICONS: Record<SettingsSection, LucideIcon> = {
  account: Settings,
  general: Settings,
  agents: Bot,
  personas: SquareUserRound,
  models: Sparkles,
  voice: Mic,
  notifications: Bell,
  worktrees: GitBranch,
  'computer-use': Monitor,
  'keyboard-shortcuts': Keyboard,
  integrations: Link2,
  plugins: LayoutGrid,
  connections: Unplug,
};

/** What the navigation shows besides the sections: who is signed in, counts and states. */
export type SettingsNavigationModel = {
  displayName: string;
  /** What a row shows at its end (`Claude Code`, `4`). */
  details: Partial<Record<SettingsSection, string>>;
  /** Connections to sign in to again: an amber dot on Connections. */
  reconnect: number;
  versionLine: string;
  /** Whether the server runs on this machine (Computer use). */
  local: boolean;
};

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const letters =
    words.length > 1
      ? `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`
      : (words[0] ?? '').slice(0, 2);
  return letters.toUpperCase() || '?';
}

export function ProfileAvatar({ name, size = 'md' }: { name: string; size?: 'md' | 'lg' | 'xl' }) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-control font-semibold text-foreground',
        size === 'md' && 'size-9 text-sm',
        size === 'lg' && 'size-16 text-xl',
        size === 'xl' && 'size-20 text-2xl',
      )}
    >
      {initials(name)}
    </span>
  );
}

function NavRow({
  active,
  icon: Icon,
  label,
  detail,
  dot,
  onSelect,
}: {
  active: boolean;
  icon: LucideIcon;
  label: string;
  detail?: string | undefined;
  dot?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group flex h-9 w-full items-center gap-3 rounded-control px-3 text-left type-ui transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        // The charter's selected row: the accent tint with the accent ink.
        active
          ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground'
          : 'text-foreground/90 hover:bg-foreground/5',
      )}
      onClick={onSelect}
      type="button"
    >
      <Icon
        aria-hidden
        className="size-4 shrink-0 text-muted-foreground group-aria-[current=page]:text-current"
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {detail && (
        <span className="shrink-0 truncate font-normal text-muted-foreground type-caption">
          {detail}
        </span>
      )}
      {dot && (
        <>
          <span aria-hidden className="size-1.5 rounded-full bg-attention" />
          <span className="sr-only">A connection to reconnect</span>
        </>
      )}
    </button>
  );
}

function GroupTitle({ children }: { children: ReactNode }) {
  return <p className="mt-4 mb-1 px-3 text-muted-foreground type-caption">{children}</p>;
}

/**
 * Settings' own column (D16): back to the app (Esc), search, the profile, the personal
 * sections, the current workspace's, Plugins & connections, then Sign out and the version.
 */
export function SettingsNavigation({
  active,
  model,
  onSelect,
  onBack,
  onSignOut,
}: {
  active: SettingsSection;
  model: SettingsNavigationModel;
  onSelect: (section: SettingsSection) => void;
  onBack: () => void;
  onSignOut: () => void;
}) {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);
  const sections = useMemo(
    () => availableSections({ phone: false, local: model.local }),
    [model.local],
  );
  const shownSections = searchSettings(sections, query);
  const byGroup = (group: SettingsSectionInfo['group']) =>
    shownSections.filter((section) => section.group === group && section.id !== 'account');
  const personal = byGroup('personal');
  const organization = byGroup('organization');
  const plugins = byGroup('plugins');
  const account = shownSections.find((section) => section.id === 'account');
  const nothing = shownSections.length === 0 && query.trim() !== '';

  return (
    <nav
      aria-label="Settings sections"
      className="flex h-full w-64 shrink-0 flex-col bg-desktop-sidebar px-2.5 pb-3"
    >
      <div className="flex h-12 shrink-0 items-end pb-1" data-electron-drag-region>
        <button
          className="flex h-9 w-full items-center gap-3 rounded-xl px-3 text-foreground type-ui hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onBack}
          type="button"
        >
          <ArrowLeft aria-hidden className="size-4 text-muted-foreground" />
          <span className="flex-1 text-left">Back to app</span>
          <kbd className="rounded-md border border-foreground/15 px-1.5 font-sans text-muted-foreground type-caption">
            Esc
          </kbd>
        </button>
      </div>
      <div className="mt-1.5 flex h-9 shrink-0 items-center gap-2.5 rounded-xl bg-control px-3">
        <Search aria-hidden className="size-4 text-muted-foreground" />
        <Input
          appearance="document"
          aria-label="Search settings"
          className="min-w-0 flex-1 bg-transparent text-foreground type-ui outline-none placeholder:text-muted-foreground"
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && query !== '') {
              // Clears the search first; the next Esc lets go of the field (Settings keeps it).
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              setQuery('');
            }
            if (event.key === 'Enter') {
              const first = [...personal, ...organization, ...plugins][0] ?? account;
              if (first !== undefined) onSelect(first.id);
            }
          }}
          placeholder="Search settings"
          ref={searchRef}
          type="search"
          value={query}
        />
      </div>
      <div className="mt-2 min-h-0 flex-1 overflow-y-auto">
        {account !== undefined && (
          <button
            aria-current={active === 'account' ? 'page' : undefined}
            className={cn(
              'mb-2 flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active === 'account' && 'bg-control',
            )}
            onClick={() => onSelect('account')}
            type="button"
          >
            <ProfileAvatar name={model.displayName} />
            <span className="min-w-0">
              <span className="block truncate font-semibold text-foreground type-ui">
                {model.displayName}
              </span>
              <span className="block truncate text-muted-foreground type-caption">
                Account · Crowdex Cloud
              </span>
            </span>
          </button>
        )}
        {personal.map((section) => (
          <NavRow
            active={active === section.id}
            detail={model.details[section.id]}
            icon={SECTION_ICONS[section.id]}
            key={section.id}
            label={section.title}
            onSelect={() => onSelect(section.id)}
          />
        ))}
        {organization.length > 0 && (
          <>
            <GroupTitle>Organization</GroupTitle>
            {organization.map((section) => (
              <NavRow
                active={active === section.id}
                detail={model.details[section.id]}
                icon={SECTION_ICONS[section.id]}
                key={section.id}
                label={section.title}
                onSelect={() => onSelect(section.id)}
              />
            ))}
          </>
        )}
        {plugins.length > 0 && (
          <>
            <GroupTitle>Plugins &amp; connections</GroupTitle>
            {plugins.map((section) => (
              <NavRow
                active={active === section.id}
                dot={section.id === 'connections' && model.reconnect > 0}
                icon={SECTION_ICONS[section.id]}
                key={section.id}
                label={section.title}
                onSelect={() => onSelect(section.id)}
              />
            ))}
          </>
        )}
        {nothing && <p className="px-3 py-2 text-muted-foreground type-ui">No settings found.</p>}
      </div>
      <button
        className="mt-2 flex h-9 shrink-0 items-center gap-3 rounded-xl px-3 text-destructive type-ui hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={onSignOut}
        type="button"
      >
        <LogOut aria-hidden className="size-4 rotate-180" />
        Sign out
      </button>
      <p className="mt-1 shrink-0 truncate px-3 text-muted-foreground type-caption">
        {model.versionLine}
      </p>
    </nav>
  );
}
